
import { Routes, Route, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEffect } from 'react';

import { AuthProvider } from './contexts/AuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { CustomerPortalProvider } from './contexts/CustomerPortalContext';
import { PartnerAuthProvider } from './contexts/PartnerAuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import CustomerProtectedRoute from './components/CustomerProtectedRoute';
import ManagedAccountGuard from './components/customer/ManagedAccountGuard';
import PartnerProtectedRoute from './components/partner/PartnerProtectedRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import TenantsPage from './pages/TenantsPage';
import TenantDetailPage from './pages/TenantDetailPage';
import ClientAccountsPage from './pages/ClientAccountsPage';
import ClientAccountDetailPage from './pages/ClientAccountDetailPage';
import PartnersPage from './pages/PartnersPage';
import PartnerDetailAdminPage from './pages/PartnerDetailAdminPage';
import CreditPackagesPage from './pages/CreditPackagesPage';
import DeploymentsPage from './pages/DeploymentsPage';
import UsersPage from './pages/UsersPage';
import SizesPage from './pages/SizesPage';
import ProfilePage from './pages/ProfilePage';

// Customer portal pages
import CustomerLandingPage from './pages/customer/CustomerLandingPageV2';
import CustomerLoginPage from './pages/customer/CustomerLoginPageV2';
import CustomerRegisterPage from './pages/customer/CustomerRegisterPageV2';
import CustomerPlansPage from './pages/customer/CustomerPlansPage';
import CustomerSubscriptionPage from './pages/customer/CustomerSubscriptionPageV2';
import CustomerBillingPage from './pages/customer/CustomerBillingPage';
import CustomerProfilePage from './pages/customer/CustomerProfilePage';
import CustomerSupportPage from './pages/customer/CustomerSupportPage';
import CustomerCheckoutPage from './pages/customer/CustomerCheckoutPageV2';
import CustomerAccountPage from './pages/customer/CustomerAccountPage';
import CustomerCreditsHistoryPage from './pages/customer/CustomerCreditsHistoryPage';
import CustomerImpersonationEntry from './pages/customer/CustomerImpersonationEntry';
// Guía global del portal del cliente (didáctica, bilingüe): qué es un empleado
// IA, tu cuenta, usuarios, contratación, créditos, almacenamiento y partner.
import CustomerHelpPage from './pages/customer/help/CustomerHelpPage';

// Partner portal pages (F2 — portal comercial propio en /partner/*)
import PartnerLoginPage from './pages/partner/PartnerLoginPage';
import PartnerProgramPage from './pages/partner/PartnerProgramPage';
import PartnerDashboardPage from './pages/partner/PartnerDashboardPage';
import PartnerClientsPage from './pages/partner/PartnerClientsPage';
import PartnerClientDetailPage from './pages/partner/PartnerClientDetailPage';
import PartnerBillingPage from './pages/partner/PartnerBillingPage';
import PartnerPackagesPage from './pages/partner/PartnerPackagesPage';
import PartnerHelpPage from './pages/partner/help/PartnerHelpPage';

// Component to handle Paddle checkout redirect (_ptxn parameter)
function PaddleRedirectHandler() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    const transactionId = searchParams.get('_ptxn');
    if (transactionId) {
      // CRITICAL FIX: Redirect to CUSTOMER checkout, not reseller checkout
      navigate('/customer/checkout', { 
        state: { 
          transactionId: transactionId,
          fromPaddleRedirect: true
        },
        replace: true 
      });
    }
  }, [searchParams, navigate]);
  
  return null;
}

function App() {
  return (
    <AuthProvider>
      <CustomerAuthProvider>
       <CustomerPortalProvider>
       <PartnerAuthProvider>
        <PaddleRedirectHandler />
        <Routes>
          {/* Customer portal routes (public) */}
          <Route path="/customer" element={<CustomerLandingPage />} />
          <Route path="/customer/login" element={<CustomerLoginPage />} />
          <Route path="/customer/register" element={<CustomerRegisterPage />} />
          {/* Entrada de impersonación «Ver como cliente» (token por query) */}
          <Route path="/customer/impersonate" element={<CustomerImpersonationEntry />} />
          
          {/* Customer portal routes (protected). Las páginas de billing/pagos/
              planes Paddle van tras ManagedAccountGuard: en cuentas gestionadas
              por un partner (o en impersonación) NO existen — redirigen a
              /customer/account. */}
          <Route
            path="/customer/plans"
            element={
              <CustomerProtectedRoute>
                <ManagedAccountGuard>
                  <CustomerPlansPage />
                </ManagedAccountGuard>
              </CustomerProtectedRoute>
            }
          />
          {/* Suscripción = el PLAN DE CRÉDITOS de la cuenta (ya no el producto
              Paddle de tenants). NO va tras ManagedAccountGuard a propósito: una
              cuenta gestionada por un partner DEBE poder ver su plan y su origen
              (se lo asigna su proveedor); lo que no puede es contratar/pagar, y
              de eso se ocupa la propia página con managedByReseller. */}
          <Route
            path="/customer/subscription"
            element={
              <CustomerProtectedRoute>
                <CustomerSubscriptionPage />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/billing"
            element={
              <CustomerProtectedRoute>
                <ManagedAccountGuard>
                  <CustomerBillingPage />
                </ManagedAccountGuard>
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/profile"
            element={
              <CustomerProtectedRoute>
                <CustomerProfilePage />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/support"
            element={
              <CustomerProtectedRoute>
                <CustomerSupportPage />
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/checkout"
            element={
              <CustomerProtectedRoute>
                <ManagedAccountGuard>
                  <CustomerCheckoutPage />
                </ManagedAccountGuard>
              </CustomerProtectedRoute>
            }
          />
          <Route
            path="/customer/account"
            element={
              <CustomerProtectedRoute>
                <CustomerAccountPage />
              </CustomerProtectedRoute>
            }
          />
          {/* Historial de créditos (tabla larga del ledger, paginada). NO va
              tras ManagedAccountGuard: la sección de créditos existe también en
              las cuentas gestionadas por un partner. */}
          <Route
            path="/customer/creditos"
            element={
              <CustomerProtectedRoute>
                <CustomerCreditsHistoryPage />
              </CustomerProtectedRoute>
            }
          />
          {/* Ayuda global del portal. NO va tras ManagedAccountGuard: la
              guía es útil (y necesaria) también en cuentas gestionadas por un
              partner — de hecho tiene una sección dedicada a explicarlas. */}
          <Route
            path="/customer/help"
            element={
              <CustomerProtectedRoute>
                <CustomerHelpPage />
              </CustomerProtectedRoute>
            }
          />

          {/* Partner portal (F2) — auth propia, look comercial propio */}
          <Route path="/partner/login" element={<PartnerLoginPage />} />
          {/* Landing COMERCIAL del programa de partners: pública, sin auth.
              Va ANTES del comodín /partner/* (si no, redirigiría a /partner). */}
          <Route path="/partner/programa" element={<PartnerProgramPage />} />
          <Route
            path="/partner"
            element={
              <PartnerProtectedRoute>
                <PartnerDashboardPage />
              </PartnerProtectedRoute>
            }
          />
          <Route
            path="/partner/clients"
            element={
              <PartnerProtectedRoute>
                <PartnerClientsPage />
              </PartnerProtectedRoute>
            }
          />
          <Route
            path="/partner/clients/:id"
            element={
              <PartnerProtectedRoute>
                <PartnerClientDetailPage />
              </PartnerProtectedRoute>
            }
          />
          {/* Guía global del portal partner. Va ANTES del comodín /partner/*
              (si no, redirigiría a /partner) y tras el protegido: la guía habla
              de tu bolsa, tu techo y tus márgenes — no es contenido público. */}
          <Route
            path="/partner/ayuda"
            element={
              <PartnerProtectedRoute>
                <PartnerHelpPage />
              </PartnerProtectedRoute>
            }
          />
          {/* Catálogo de paquetes con SU lectura (coste, referencia, margen) */}
          <Route
            path="/partner/packages"
            element={
              <PartnerProtectedRoute>
                <PartnerPackagesPage />
              </PartnerProtectedRoute>
            }
          />
          <Route
            path="/partner/billing"
            element={
              <PartnerProtectedRoute>
                <PartnerBillingPage />
              </PartnerProtectedRoute>
            }
          />
          <Route path="/partner/*" element={<Navigate to="/partner" replace />} />

          {/* Reseller portal routes (public) */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Reseller portal routes (protected) */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/customers" element={<CustomersPage />} />
                    <Route path="/tenants" element={<TenantsPage />} />
                    <Route path="/tenants/:id" element={<TenantDetailPage />} />
                    <Route path="/client-accounts" element={<ClientAccountsPage />} />
                    <Route path="/client-accounts/:id" element={<ClientAccountDetailPage />} />
                    {/* Partners (resellers) — administración /resellers/* */}
                    <Route path="/partners" element={<PartnersPage />} />
                    <Route path="/partners/:id" element={<PartnerDetailAdminPage />} />
                    {/* Catálogo de paquetes de créditos: precios y textos
                        comerciales del producto «empleados IA» (editable). */}
                    <Route path="/credit-packages" element={<CreditPackagesPage />} />
                    <Route path="/deployments" element={<DeploymentsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route 
                      path="/users" 
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <UsersPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/sizes" 
                      element={
                        <ProtectedRoute requiredRole="super_admin">
                          <SizesPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>

        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
       </PartnerAuthProvider>
       </CustomerPortalProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );
}

export default App;